import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useStore } from "../lib/store";
import Logo from "./Logo";
import { Search, Heart, ShoppingBag, User, Menu, X, ChevronDown, ChevronRight, Instagram, Truck } from "lucide-react";

const NAV = [
  { label: "Shop by Categories", to: "/collections/shop-all", mega: true },
  { label: "Water Bottles", to: "/collections/water-bottles" },
  { label: "Lunchboxes", to: "/collections/lunchboxes" },
  { label: "Stationery", to: "/collections/stationery" },
  { label: "Kids' Luggage", to: "/collections/luggage" },
  { label: "Gift Sets", to: "/collections/gifts" },
  { label: "New Arrivals", to: "/collections/new-arrivals" },
];

function Mega({ cats, open, onClose, onHover }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.25, ease: [0.3, 1, 0.3, 1] }}
          onMouseEnter={() => onHover(true)} onMouseLeave={() => onHover(false)}
          className="absolute left-0 right-0 top-full hidden lg:block z-50 bg-white border-b border-line shadow-[0_24px_40px_-24px_rgba(7,40,53,0.25)]" data-testid="mega-menu">
          <div className="mx-auto max-w-[1700px] px-6 xl:px-14 py-9 grid grid-cols-12 gap-10" onClick={(e) => e.stopPropagation()}>
            <div className="col-span-5">
              <p className="label mb-5">Shop by category</p>
              <ul className="grid grid-cols-2 gap-x-8 gap-y-4">
                {cats.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/collections/${c.slug}`} onClick={onClose} data-testid={`mega-cat-${c.slug}`} className="flex items-center gap-3 group">
                      <img src={c.image} alt="" width="48" height="48" className="w-12 h-12 rounded-full object-cover bg-surface" loading="lazy" />
                      <span className="text-sm group-hover:text-coral transition-colors">{c.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-3">
              <p className="label mb-5">Featured</p>
              <ul className="space-y-3 text-sm">
                {[["Bestsellers", "/collections/shop-all"], ["New arrivals", "/collections/new-arrivals"], ["Gift sets & bundles", "/collections/gifts"], ["Little explorers' luggage", "/collections/luggage"], ["School-day essentials", "/collections/shop-all"]].map(([label, to]) => (
                  <li key={label}><Link to={to} onClick={onClose} className="hover:text-coral transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>
            <div className="col-span-4">
              <Link to="/products/big-smile-gift-set" onClick={onClose} data-testid="mega-featured-collection" className="group relative block rounded-2xl overflow-hidden bg-sage">
                <img src="https://static.prod-images.emergentagent.com/jobs/047283dd-777d-49aa-87e9-fa629b5cc4bd/images/e5e419e66212c86c4a1327b0fded19b7e7cbdda003e7f9b7ebadaa2e22cf460b.jpeg"
                  alt="Big Smile gift set — insulated bottle and bento box tied with a ribbon" width="420" height="240"
                  className="w-full h-56 object-cover group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-forest/80 to-transparent text-white">
                  <p className="eyebrow text-white/90 text-base">Gift Guide</p>
                  <p className="font-display text-2xl mt-0.5">Big Smile Gift Sets</p>
                </div>
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MobileDrawer({ open, onClose, cats }) {
  const [expanded, setExpanded] = useState(null);
  const closeRef = useRef(null);
  useEffect(() => { if (open) setTimeout(() => closeRef.current?.focus(), 100); }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 bg-forest/50 z-[60]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.aside role="dialog" aria-label="Menu" data-testid="mobile-drawer"
            className="fixed left-0 top-0 bottom-0 w-[86vw] max-w-sm bg-white z-[65] flex flex-col"
            initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.4, ease: [0.3, 1, 0.3, 1] }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <Logo />
              <button ref={closeRef} onClick={onClose} aria-label="Close menu" data-testid="mobile-drawer-close" className="p-2 rounded-full hover:bg-surface"><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto px-5 py-4" aria-label="Mobile navigation">
              <button onClick={() => setExpanded(expanded === "cats" ? null : "cats")} aria-expanded={expanded === "cats"}
                className="w-full flex items-center justify-between text-base py-3.5 border-b border-line">
                Shop by Categories <ChevronDown className={`w-5 h-5 transition-transform ${expanded === "cats" ? "rotate-180" : ""}`} />
              </button>
              {expanded === "cats" && (
                <ul className="py-2 pl-1">
                  {cats.map((c) => (
                    <li key={c.slug}>
                      <Link to={`/collections/${c.slug}`} onClick={onClose} className="flex items-center gap-3 py-2.5 text-sm">
                        <img src={c.image} alt="" width="40" height="40" className="w-10 h-10 rounded-full object-cover bg-surface" loading="lazy" /> {c.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {NAV.slice(1).map((n) => (
                <Link key={n.to} to={n.to} onClick={onClose} className="block text-base py-3.5 border-b border-line">{n.label}</Link>
              ))}
              <p className="label mt-7 mb-2">Help</p>
              {[["Track Order", "/track-order"], ["My Orders", "/account"], ["Wishlist", "/wishlist"], ["Contact", "/contact"], ["FAQs", "/faqs"], ["Returns & Exchange", "/policies/returns"], ["Privacy Policy", "/policies/privacy"], ["Terms of Service", "/policies/terms"]].map(([label, to]) => (
                <Link key={to} to={to} onClick={onClose} className="flex items-center justify-between py-2.5 text-sm text-subtext">
                  {label} <ChevronRight className="w-4 h-4 text-subtext/60" />
                </Link>
              ))}
            </nav>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function SearchField({ onOpen, className = "" }) {
  return (
    <button type="button" onClick={onOpen} aria-label="Search" data-testid="search-button"
      className={`field flex items-center justify-between text-left text-subtext text-sm hover:bg-[#E6E6E6] transition-colors ${className}`}>
      <span>What are you looking for?</span>
      <Search className="w-5 h-5 text-charcoal shrink-0" />
    </button>
  );
}

export default function Header() {
  const { settings, cartCount, setCartOpen, setSearchOpen, wishlist } = useStore();
  const [megaOpen, setMegaOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [cats, setCats] = useState([]);
  const megaRef = useRef(null);
  const closeTimer = useRef(null);
  const hoverMega = (on) => {
    clearTimeout(closeTimer.current);
    if (on) setMegaOpen(true);
    else closeTimer.current = setTimeout(() => setMegaOpen(false), 160);
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/collections`).then((r) => r.json()).then(setCats).catch(() => {});
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 140);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMegaOpen(false); };
    const onClick = (e) => { if (megaRef.current && !megaRef.current.contains(e.target) && !e.target.closest("[data-testid='mega-menu']")) setMegaOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("click", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("click", onClick); };
  }, []);

  const Icons = ({ light = false, sfx = "" }) => (
    <div className={`flex items-center gap-1 sm:gap-4 ${light ? "text-white" : "text-charcoal"}`}>
      <Link to="/track-order" data-testid={`header-track-order${sfx}`} className="hidden xl:flex items-center gap-2 text-sm hover:text-coral transition-colors"><Truck className="w-5 h-5" /> Track Order</Link>
      <Link to="/account" aria-label="My orders" data-testid={`account-link${sfx}`} className="hidden md:flex items-center gap-2 text-sm hover:text-coral transition-colors"><User className="w-5 h-5" /> <span className="hidden xl:inline">Sign in / My Orders</span></Link>
      <Link to="/wishlist" aria-label={`Wishlist (${wishlist.length} items)`} data-testid={`wishlist-link${sfx}`} className="relative p-2.5 rounded-full hover:bg-surface/60 transition-colors">
        <Heart className="w-5 h-5" />
        {wishlist.length > 0 && <span data-testid={`wishlist-count${sfx}`} className="absolute -top-0.5 -right-0.5 bg-coral text-white text-[10px] min-w-[18px] h-[18px] rounded-full flex items-center justify-center">{wishlist.length}</span>}
      </Link>
      <button onClick={() => setCartOpen(true)} aria-label={`Open bag (${cartCount} items)`} data-testid={`cart-button${sfx}`} className={`relative w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${light ? "border-white/30 hover:bg-white/10" : "border-line hover:bg-surface"}`}>
        <ShoppingBag className="w-5 h-5" />
        {cartCount > 0 && <span data-testid={`cart-count-badge${sfx}`} className="absolute -top-1 -right-1 bg-coral text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center animate-pop">{cartCount}</span>}
      </button>
    </div>
  );

  return (
    <>
      {/* Utility bar */}
      <div className="hidden lg:block bg-surface text-[13px] border-b border-line" data-testid="announcement-bar" role="region" aria-label="Announcement">
        <div className="mx-auto max-w-[1700px] px-6 xl:px-14 h-9 flex items-center justify-between">
          <nav className="flex items-center gap-5" aria-label="Help">
            <Link to="/faqs" className="hover:text-coral transition-colors">Help Center</Link>
            <Link to="/track-order" className="hover:text-coral transition-colors">Track Order</Link>
            <Link to="/contact" className="hover:text-coral transition-colors">Contact</Link>
          </nav>
          <p className="text-center">{settings?.announcement || "Free shipping on orders over ₹1,299 — across India"}</p>
          <div className="flex items-center gap-4">
            <span className="text-subtext">India (INR ₹)</span>
            <a href="https://instagram.com/havenhives_" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-coral transition-colors"><Instagram className="w-4 h-4" /></a>
          </div>
        </div>
      </div>

      <div className="lg:hidden bg-forest text-white text-center text-[13px] h-9 flex items-center justify-center px-4 truncate" data-testid="announcement-bar-mobile">{settings?.announcement || "Free shipping on orders over ₹1,299 — across India"}</div>
      <header className={`sticky top-0 z-50 ${scrolled ? "" : "relative"}`} data-testid="site-header">
        {/* Full header (not scrolled) */}
        <div className={`bg-white border-b border-line ${scrolled ? "hidden" : "block"}`}>
          <div className="mx-auto max-w-[1700px] px-4 sm:px-6 xl:px-14 h-[64px] lg:h-[72px] grid grid-cols-[auto_1fr_auto] lg:grid-cols-3 items-center gap-3">
            <div className="flex items-center gap-2">
              <button className="lg:hidden p-2 -ml-2" onClick={() => setDrawerOpen(true)} aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="w-6 h-6" /></button>
              <SearchField onOpen={() => setSearchOpen(true)} className="hidden lg:flex w-[350px]" />
              <div className="lg:hidden"><Logo /></div>
            </div>
            <div className="hidden lg:flex justify-center"><Logo /></div>
            <div className="flex justify-end"><Icons /></div>
          </div>
          <div className="lg:hidden px-4 pb-3"><SearchField onOpen={() => setSearchOpen(true)} className="flex w-full h-11" /></div>
          <nav className="hidden lg:block bg-forest text-white" aria-label="Main navigation">
            <div className="mx-auto max-w-[1700px] px-6 xl:px-14 h-[50px] flex items-center justify-center gap-1">
              {NAV.map((n) => n.mega ? (
                <div key={n.label} ref={megaRef} onMouseEnter={() => hoverMega(true)} onMouseLeave={() => hoverMega(false)}>
                  <button aria-expanded={megaOpen} aria-haspopup="true" data-testid="mega-menu-trigger"
                    onClick={() => { clearTimeout(closeTimer.current); setMegaOpen(true); }} onFocus={() => setMegaOpen(true)}
                    className={`px-4 py-2 text-[15px] flex items-center gap-1 whitespace-nowrap transition-colors hover:text-white/70 ${megaOpen ? "text-white/70" : ""}`}>
                    {n.label} <ChevronDown className={`w-4 h-4 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
              ) : (
                <NavLink key={n.to} to={n.to} className={({ isActive }) => `px-4 py-2 text-[15px] whitespace-nowrap transition-colors hover:text-white/70 ${isActive ? "text-white/70" : ""}`}>{n.label}</NavLink>
              ))}
              <Link to="/collections/shop-all?sort=price-asc" data-testid="nav-on-sale" className="ml-2 bg-coral text-white text-[15px] px-3.5 py-1.5 rounded-full hover:bg-coral-dark transition-colors">On Sale</Link>
            </div>
          </nav>
        </div>

        {/* Compact sticky header (scrolled) */}
        <div className={`bg-white/95 backdrop-blur border-b border-line shadow-[0_8px_30px_-18px_rgba(7,40,53,0.25)] ${scrolled ? "block" : "hidden"}`}>
          <div className="mx-auto max-w-[1700px] px-4 sm:px-6 xl:px-14 h-[72px] grid grid-cols-[auto_1fr_auto] lg:grid-cols-3 items-center gap-3">
            <div className="flex items-center gap-3">
              <button className="p-2 -ml-2" onClick={() => setDrawerOpen(true)} aria-label="Open menu" data-testid="compact-menu-button"><Menu className="w-6 h-6" /></button>
              <SearchField onOpen={() => setSearchOpen(true)} className="hidden lg:flex w-[350px]" />
              <div className="lg:hidden"><Logo /></div>
              <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search" className="lg:hidden p-2"><Search className="w-5 h-5" /></button>
            </div>
            <div className="hidden lg:flex justify-center"><Logo /></div>
            <div className="flex justify-end"><Icons sfx="-compact" /></div>
          </div>
        </div>
        <Mega cats={cats} open={megaOpen} onClose={() => setMegaOpen(false)} onHover={hoverMega} />
      </header>
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} cats={cats} />
    </>
  );
}
